import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate } from 'react-router-dom';

import { SuspenseLoading } from '@/components/LoadingIndicator/SuspenseLoading';
import { PageError } from '@/components/PageError/PageError';
import { LandingPage } from '@/pages/LandingPage/LandingPage';
import { MyDCRsPage } from '@/pages/MyDCRsPage/MyDCRsPage';
import { useRouteActions } from '@/utils/navigation/useRouteActions';

import { Permission } from '../store/auth/auth.types';
import { useHasPermission } from '../store/auth/authSelectors';

import { paths } from './paths';
import { PrivateRoute } from './PrivateRoute/PrivateRoute';
import { PublicRoute } from './PublicRoute/PublicRoute';

const MagicLinkVerification = lazy(() =>
	import('@/pages/LoginPage/MagicLinkVerification/MagicLinkVerification').then(
		m => ({ default: m.MagicLinkVerification })
	)
);
const LoginPage = lazy(() =>
	import('@/pages/LoginPage/LoginPage').then(m => ({ default: m.LoginPage }))
);

const HcpSearchPage = lazy(() =>
	import('@/pages/HcpSearchPage/HcpSearchPage').then(m => ({
		default: m.HcpSearchPage
	}))
);
const HcpProfilePage = lazy(() =>
	import('@/pages/HcpProfilePage/HcpProfilePage').then(m => ({
		default: m.HcpProfilePage
	}))
);
const HcoSearchPage = lazy(() =>
	import('@/pages/HcoSearchPage/HcoSearchPage').then(m => ({
		default: m.HcoSearchPage
	}))
);
const HcoProfilePage = lazy(() =>
	import('@/pages/HcoProfilePage/HcoProfilePage').then(m => ({
		default: m.HcoProfilePage
	}))
);

export const AppRoutes = () => {
	const { t } = useTranslation();
	const dcrEnabled = useHasPermission(Permission.DcrEnabled);
	useRouteActions();

	return (
		<Routes>
			<Route
				path={paths.magicLinkVerification}
				element={
					<PublicRoute>
						<MagicLinkVerification />
					</PublicRoute>
				}
			/>

			{/* Public route for login */}
			<Route
				path={paths.login}
				element={
					<PublicRoute>
						<LoginPage />
					</PublicRoute>
				}
			/>

			{/* Redirect home path to HCPs search */}
			<Route
				path={paths.home}
				element={
					<PrivateRoute>
						<LandingPage />
					</PrivateRoute>
				}
			/>

			{/* Private route for search page */}
			<Route
				path={paths.search}
				element={
					<PrivateRoute>
						<Navigate to={paths.searchHcp} replace />
					</PrivateRoute>
				}
			/>

			{/* Private route for HCPs search */}
			<Route
				path={paths.searchHcp}
				element={
					<Suspense fallback={<SuspenseLoading />}>
						<PrivateRoute>
							<HcpSearchPage />
						</PrivateRoute>
					</Suspense>
				}
			/>

			{/* Private route for HCP profile */}
			<Route
				path={paths.hcp}
				element={
					<PrivateRoute>
						<HcpProfilePage />
					</PrivateRoute>
				}
			/>

			{/* Private route for HCOs search */}
			<Route
				path={paths.searchHco}
				element={
					<Suspense fallback={<SuspenseLoading />}>
						<PrivateRoute>
							<HcoSearchPage />
						</PrivateRoute>
					</Suspense>
				}
			/>

			{/* Private route for site profile */}
			<Route
				path={paths.hco}
				element={
					<PrivateRoute>
						<HcoProfilePage />
					</PrivateRoute>
				}
			/>

			{/* Private route for viewing DCRs */}
			<Route
				path={paths.dcrs}
				element={
					<PrivateRoute isAllowed={dcrEnabled}>
						<MyDCRsPage />
					</PrivateRoute>
				}
			/>

			{/* Wildcard route for 404 - wrapped in PrivateRoute */}
			<Route
				path={paths.wildcard}
				element={
					<PrivateRoute>
						<PageError
							description={t('The page you are looking for does not exist.')}
						/>
					</PrivateRoute>
				}
			/>
		</Routes>
	);
};
